import { useEffect, useMemo, useState } from 'react';
import { permissionsApi, rolesApi } from '../../lib/api';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { Modal } from '../../components/Modal';
import { Field, TextInput } from '../../components/Field';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Can } from '../../lib/permissions';
import { useShortcuts } from '../../lib/keys';
import { useUiStore } from '../../stores/ui';
import type { PermissionCatalogueEntry, Role } from '../../lib/types';

export function RolesPage() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [catalogue, setCatalogue] = useState<PermissionCatalogueEntry[]>([]);
  const [editing, setEditing] = useState<Role | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const pushToast = useUiStore((s) => s.pushToast);

  function load() {
    rolesApi.list().then(({ items }) => setRoles(items)).catch(setError);
    permissionsApi.catalogue().then(({ items }) => setCatalogue(items)).catch(setError);
  }
  useEffect(load, []);

  useShortcuts('Roles', [
    { id: 'new-role', bindings: [{ key: 'n', alt: true }], description: 'New role', handler: () => { setEditing(null); setFormOpen(true); } },
  ]);

  const columns: DataTableColumn<Role>[] = [
    { key: 'name', header: 'Role', render: (r) => <span>{r.name}{r.isSystem && <span className="ml-1.5 kbd">system</span>}</span> },
    { key: 'perms', header: 'Permissions', render: (r) => <span className="text-[var(--text-dim)]">{r.permissions.length}</span> },
  ];

  return (
    <div className="flex h-full flex-col p-4">
      <div className="mb-3 flex items-center justify-between border-b border-[var(--border)] pb-2">
        <h1 className="text-[14px] font-semibold">Roles</h1>
        <Can permission="admin:role:create">
          <button onClick={() => { setEditing(null); setFormOpen(true); }} className="rounded-[3px] border border-[var(--border)] px-2 py-1 text-[12px] hover:bg-[var(--bg-hover)]">
            New role <span className="kbd ml-1">Alt+N</span>
          </button>
        </Can>
      </div>
      {error ? <ErrorBanner error={error} onDismiss={() => setError(null)} /> : null}
      <div className="min-h-0 flex-1">
        <DataTable columns={columns} rows={roles} getRowKey={(r) => r.id} onActivate={(r) => { setEditing(r); setFormOpen(true); }} className="h-full" />
      </div>

      <Modal open={formOpen} onClose={() => setFormOpen(false)} title={editing ? `Edit — ${editing.name}` : 'New role'} widthClassName="max-w-xl">
        <RoleForm
          key={editing?.id ?? 'new'}
          role={editing}
          catalogue={catalogue}
          onDone={(msg) => { setFormOpen(false); pushToast({ kind: 'success', message: msg }); load(); }}
          onError={setError}
        />
      </Modal>
    </div>
  );
}

function RoleForm({ role, catalogue, onDone, onError }: { role: Role | null; catalogue: PermissionCatalogueEntry[]; onDone: (m: string) => void; onError: (e: unknown) => void }) {
  const [name, setName] = useState(role?.name ?? '');
  const [code, setCode] = useState(role?.code ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [permissions, setPermissions] = useState<string[]>(role?.permissions ?? []);
  const [busy, setBusy] = useState(false);
  const readOnly = Boolean(role?.isSystem);

  const domains = useMemo(() => {
    const byDomain = new Map<string, PermissionCatalogueEntry[]>();
    for (const p of catalogue) {
      const list = byDomain.get(p.domain) ?? [];
      list.push(p);
      byDomain.set(p.domain, list);
    }
    return [...byDomain.entries()];
  }, [catalogue]);

  function toggle(key: string) {
    setPermissions((prev) => (prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      if (role) {
        await rolesApi.update(role.id, { revision: role.revision, code, name, description: description || null });
        const saved = await rolesApi.setPermissions(role.id, permissions);
        onDone(`${saved.name} saved.`);
      } else {
        const saved = await rolesApi.create({ code, name, description: description || null, permissions });
        onDone(`${saved.name} created.`);
      }
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      {readOnly && <p className="text-[11.5px] text-[var(--warn)]">System roles can't be edited. Clone this into a custom role to change it.</p>}
      <Field label="Role name" htmlFor="rf-name" required>
        <TextInput id="rf-name" autoFocus={!role} value={name} onChange={(e) => setName(e.target.value)} required disabled={readOnly} />
      </Field>
      <Field label="Role code" htmlFor="rf-code" required hint="Lower-case letters, digits, and underscores; it cannot be changed for system roles.">
        <TextInput id="rf-code" value={code} onChange={(e) => setCode(e.target.value.toLowerCase())} pattern="[a-z][a-z0-9_]*" required disabled={readOnly} />
      </Field>
      <Field label="Description" htmlFor="rf-description">
        <TextInput id="rf-description" value={description} onChange={(e) => setDescription(e.target.value)} disabled={readOnly} />
      </Field>
      <div className="max-h-72 overflow-y-auto border border-[var(--border)]">
        {domains.map(([domain, perms]) => (
          <div key={domain}>
            <div className="bg-[var(--bg-inset)] px-2 py-0.5 text-[10.5px] uppercase tracking-wide text-[var(--text-dim)]">{domain}</div>
            {perms.map((p) => (
              <label key={p.key} className="flex items-center gap-2 px-2 py-0.5 text-[12px] hover:bg-[var(--bg-hover)]">
                <input type="checkbox" checked={permissions.includes(p.key)} disabled={readOnly} onChange={() => toggle(p.key)} />
                <span className="mono">{p.key}</span>
              </label>
            ))}
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <button type="submit" disabled={busy || readOnly} className="rounded-[3px] border border-[var(--accent)] bg-[var(--accent)] px-3 py-1.5 text-[12.5px] font-medium text-[var(--accent-fg)] disabled:opacity-50">
          {busy ? 'Saving…' : role ? 'Save permissions' : 'Create role'}
        </button>
      </div>
    </form>
  );
}
